import { Send, CheckCircle, XCircle, Clock, AlertTriangle, Eye } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { StatsCard } from '@/components/dashboard/StatsCard';
import { PeriodFilter } from '@/components/dashboard/PeriodFilter';
import { DispatchesChart } from '@/components/dashboard/DispatchesChart';
import { RecentDispatchesTable } from '@/components/dashboard/RecentDispatchesTable';
import { RcsCampaignDispatchesTable } from '@/components/rcs/RcsCampaignDispatchesTable';
import { useDashboardStats } from '@/hooks/useDashboardStats';
import { useAuth } from '@/hooks/useAuth';
import { useUplogEnabled, useUplogDashboardStats } from '@/hooks/useUplog';
import { useRcsEnabled, useRcsDispatchStats } from '@/hooks/useRcs';
import { useState } from 'react';

function StatsSkeleton() {
  return (
    <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
      {Array.from({ length: 4 }).map((_, i) => (
        <Card key={i}>
          <CardContent className="pt-6">
            <Skeleton className="mb-2 h-8 w-16" />
            <Skeleton className="h-4 w-24" />
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function HermesTab({ period }: { period: string }) {
  const { data: stats, isLoading } = useDashboardStats(period as '7d' | '30d' | '90d');

  return (
    <div className="space-y-4">
      {isLoading ? (
        <StatsSkeleton />
      ) : stats ? (
        <>
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <StatsCard
              icon={Send}
              title="Total enviados"
              value={stats.dispatches.total.toLocaleString('pt-BR')}
              iconColor="text-primary"
            />
            <StatsCard
              icon={CheckCircle}
              title="Entregues"
              value={stats.dispatches.delivered.toLocaleString('pt-BR')}
              iconColor="text-green-600"
            />
            <StatsCard
              icon={XCircle}
              title="Falhas"
              value={stats.dispatches.failed.toLocaleString('pt-BR')}
              iconColor="text-destructive"
            />
            <StatsCard
              icon={Clock}
              title="Pendentes"
              value={stats.dispatches.pending.toLocaleString('pt-BR')}
              iconColor="text-yellow-600"
            />
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Disparos por dia</CardTitle>
            </CardHeader>
            <CardContent>
              <DispatchesChart data={stats.timeSeries} />
            </CardContent>
          </Card>
        </>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Disparos recentes</CardTitle>
        </CardHeader>
        <CardContent>
          <RecentDispatchesTable />
        </CardContent>
      </Card>
    </div>
  );
}

function UplogTab({ period }: { period: string }) {
  const { data: uplogStats, isLoading } = useUplogDashboardStats(period);

  if (isLoading) return <StatsSkeleton />;
  if (!uplogStats) return null;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
        <StatsCard
          icon={Clock}
          title="A disparar"
          value={(uplogStats.dispatches.pending + uplogStats.pending.total).toLocaleString('pt-BR')}
          iconColor="text-yellow-600"
        />
        <StatsCard
          icon={Send}
          title="Enviados"
          value={uplogStats.dispatches.sent.toLocaleString('pt-BR')}
          iconColor="text-blue-600"
        />
        <StatsCard
          icon={CheckCircle}
          title="Entregues"
          value={uplogStats.dispatches.delivered.toLocaleString('pt-BR')}
          iconColor="text-green-600"
        />
        <StatsCard
          icon={Eye}
          title="Lidos"
          value={uplogStats.dispatches.read.toLocaleString('pt-BR')}
          iconColor="text-purple-600"
        />
        <StatsCard
          icon={XCircle}
          title="Falhas"
          value={uplogStats.dispatches.failed.toLocaleString('pt-BR')}
          iconColor="text-destructive"
        />
      </div>

      {uplogStats.pending.vencidos > 0 && (
        <p className="flex items-center gap-1.5 text-sm text-orange-600 dark:text-orange-400">
          <AlertTriangle className="h-4 w-4" />
          {uplogStats.pending.vencidos.toLocaleString('pt-BR')} boleto
          {uplogStats.pending.vencidos !== 1 ? 's' : ''} vencido
          {uplogStats.pending.vencidos !== 1 ? 's' : ''} com reenvio semanal ativo
        </p>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Disparos por dia</CardTitle>
        </CardHeader>
        <CardContent>
          <DispatchesChart data={uplogStats.timeSeries} />
        </CardContent>
      </Card>
    </div>
  );
}

function RcsTab({ period }: { period: string }) {
  const { data: stats, isLoading } = useRcsDispatchStats(period as '7d' | '30d' | '90d');

  return (
    <div className="space-y-4">
      {isLoading ? (
        <StatsSkeleton />
      ) : stats ? (
        <>
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <StatsCard
              icon={Send}
              title="Total enviados"
              value={stats.overall.total.toLocaleString('pt-BR')}
              iconColor="text-primary"
            />
            <StatsCard
              icon={CheckCircle}
              title="Entregues"
              value={stats.overall.delivered.toLocaleString('pt-BR')}
              iconColor="text-green-600"
            />
            <StatsCard
              icon={XCircle}
              title="Falhas"
              value={stats.overall.failed.toLocaleString('pt-BR')}
              iconColor="text-destructive"
            />
            <StatsCard
              icon={Clock}
              title="Pendentes"
              value={stats.overall.pending.toLocaleString('pt-BR')}
              iconColor="text-yellow-600"
            />
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Disparos RCS por dia</CardTitle>
            </CardHeader>
            <CardContent>
              <DispatchesChart data={stats.timeSeries} />
            </CardContent>
          </Card>
        </>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Disparos RCS recentes</CardTitle>
        </CardHeader>
        <CardContent>
          <RcsCampaignDispatchesTable />
        </CardContent>
      </Card>
    </div>
  );
}

export function DashboardPage() {
  const { user } = useAuth();
  const [period, setPeriod] = useState<'7d' | '30d' | '90d'>('30d');
  const { data: uplogEnabled } = useUplogEnabled();
  const { data: rcsEnabled } = useRcsEnabled();

  const showTabs = uplogEnabled || rcsEnabled;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Bem-vindo, {user?.name}</h1>
          <p className="text-sm text-muted-foreground">Resumo da sua operação</p>
        </div>
        <PeriodFilter value={period} onChange={setPeriod} />
      </div>

      {showTabs ? (
        <Tabs defaultValue="hermes">
          <TabsList>
            <TabsTrigger value="hermes">Disparador Hermes</TabsTrigger>
            {uplogEnabled && <TabsTrigger value="uplog">UpLog — Boletos</TabsTrigger>}
            {rcsEnabled && <TabsTrigger value="rcs">RCS</TabsTrigger>}
          </TabsList>
          <TabsContent value="hermes">
            <HermesTab period={period} />
          </TabsContent>
          {uplogEnabled && (
            <TabsContent value="uplog">
              <UplogTab period={period} />
            </TabsContent>
          )}
          {rcsEnabled && (
            <TabsContent value="rcs">
              <RcsTab period={period} />
            </TabsContent>
          )}
        </Tabs>
      ) : (
        <HermesTab period={period} />
      )}
    </div>
  );
}
